import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Native } from "../platform/android/nativeBridge";
import { APP_METHODS, NATIVE_EVENTS } from "../platform/android/nativeMethods";
import { onNativeEvent, emitNativeEvent } from "../platform/android/nativeEventBus";
import { ROUTES } from "../routes/routePaths";
import useAppStore from "../store/useAppStore";

const EMPTY_STATE = {};
let isWebTrapInitialized = false;

// Initialize the web-specific browser back-button trap exactly once on boot.
const initWebHistoryTrap = () => {
  if (Native.isApp || isWebTrapInitialized) return;
  isWebTrapInitialized = true;

  // Set up dummy history states to bypass Chrome's anti-hijacking protections
  if (window.history.state?.trap !== "TRAP") {
    window.history.replaceState({ idx: 0, trap: "TRAP" }, "");
    for (let i = 1; i <= 90; i++) {
      try {
        window.history.pushState({ idx: i, trap: "TRAP" }, "");
      } catch (e) {
        console.warn("Could not push dummy history state:", e);
        break;
      }
    }
  }

  // Listen to the web back button and translate it into our native event system
  window.addEventListener("popstate", (e) => {
    if (e.state?.trap === "TRAP") {
      emitNativeEvent(NATIVE_EVENTS.BACK_PRESS, { remainingTraps: e.state.idx });
    }
  });
};

export const useNativeBackPress = ({
  mode = "back",
  steps = 1,
  route,
  respond = true,
  state = EMPTY_STATE,
  onBackPress,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDummyUser = useAppStore((state) => state.isDummyUser);

  useEffect(() => {
    // Ensure the web trap is running (safely ignores if already initialized or if running natively)
    initWebHistoryTrap();

    if (!respond) return;

    const nativeHandler = (eventData) => {
      const exitScreens = [
        ROUTES.EMAIL_VERIFICATION,
        ROUTES.PERSONAL_DETAILS,
        ROUTES.INCOME_INFO,
        ROUTES.ADDRESS_DETAILS,
        ROUTES.REFERENCE_VERIFICATION,
        ROUTES.KYC_VERIFICATION,
        ROUTES.UPLOAD_DOCUMENTS,
        ROUTES.REUPLOAD_DOCUMENTS,
        ROUTES.VIDEO_KYC,
      ];

      const shouldExitApp = isDummyUser && exitScreens.includes(location.pathname);

      // Priority 1: Component-level manual override
      if (onBackPress?.()) return;

      // Priority 2: Exiting the app
      if (mode === "exit" || shouldExitApp) {
        if (!Native.isApp) {
          // Cleanly exit the web app by skipping any remaining dummy history states
          const remainingTraps = eventData?.remainingTraps || 0;
          window.history.go(-(remainingTraps + 1));
        } else {
          Native.send(APP_METHODS.CLOSE_APP);
        }
        return;
      }

      // Priority 3: Internal Routing
      if (route) {
        navigate(route, { state });
      } else {
        navigate(-steps);
      }
    };

    const unsubscribeNative = onNativeEvent(NATIVE_EVENTS.BACK_PRESS, nativeHandler);

    return () => {
      unsubscribeNative();
    };
  }, [navigate, location.pathname, mode, steps, route, respond, state, onBackPress, isDummyUser]);
};
